import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import Grid from "../../../components/Grid";
import { MoreHorizontal, Pencil, Trash, Eye } from "lucide-react";
import { ShowBtn } from "@/components/Show";
import { CreateLoaderInput } from "../../../components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { ComponentSwitchToggleStatus } from "@/App/Courses/ActivedOrDisabled";
// import { UpdateBtn } from "../Update";
import { TableDataProps } from "@/pages/Contests";
import { Mask } from "@/utils/mask";
import {
  setStatusContests,
  getShowContests,
  deleteContestsData,
  generateInvestigationContest,
  updateContestStatus,
} from "@/Api/contests";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UpdateBtn } from "../Update";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CONTEST_STATUS = {
  CREATED: 1,
  EXPIRED: 2,
  FINISHED: 3,
  CANCELED: 4,
} as const;
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileSearch, Loader, CheckCircle, XCircle, BarChart3 } from "lucide-react";
import { SalesTotalizerModal } from "../SalesTotalizerModal";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
}: TypeTableProps<TableDataProps>) => {
  const [searchParams] = useSearchParams();
  const contestId = searchParams.get("q");
  const [openDialog, setOpenDialog] = useState<{
    type: "show" | "edit" | "delete";
    uuid: string;
  } | null>(null);
  const isDialogOpen = (type: "show" | "edit" | "delete", uuid: string) =>
    openDialog?.type === type && openDialog?.uuid === uuid;
  const handleDialogChange =
    (type: "show" | "edit" | "delete", uuid: string) => (open: boolean) =>
      setOpenDialog(open ? { type, uuid } : null);
  const { hasPermission } = usePermissions();
  const [investigationLoading, setInvestigationLoading] = useState<{
    [key: number]: boolean;
  }>({});
  const [statusUpdateLoading, setStatusUpdateLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    contestData: TableDataProps | null;
    newStatus: number;
    newStatusName: string;
  }>({
    isOpen: false,
    contestData: null,
    newStatus: 0,
    newStatusName: "",
  });
  
  const [investigationModal, setInvestigationModal] = useState<{
    isOpen: boolean;
    contestData: TableDataProps | null;
  }>({
    isOpen: false,
    contestData: null,
  });

  const [salesTotalizerModal, setSalesTotalizerModal] = useState<{
    isOpen: boolean;
    contestData: TableDataProps | null;
  }>({
    isOpen: false,
    contestData: null,
  });

  const getStatusColor = (statusId: number) => {
    switch (statusId) {
      case CONTEST_STATUS.CREATED:
        return "bg-yellow-500 text-white border-yellow-500";
      case CONTEST_STATUS.EXPIRED:
        return "bg-red-500 text-white border-red-500";
      case CONTEST_STATUS.FINISHED:
        return "bg-green-500 text-white border-green-500";
      case CONTEST_STATUS.CANCELED:
        return "bg-gray-500 text-white border-gray-500";
      default:
        return "bg-blue-500 text-white border-blue-500";
    }
  };

  const getContestRowColor = (statusId: number): string => {
    switch (statusId) {
      case CONTEST_STATUS.CREATED:
        return "bg-yellow-50 hover:bg-yellow-100 border-l-4 border-yellow-500";
      case CONTEST_STATUS.EXPIRED:
        return "bg-gray-50 hover:bg-gray-100 border-l-4 border-gray-500";
      case CONTEST_STATUS.FINISHED:
        return "bg-green-50 hover:bg-green-100 border-l-4 border-green-500";
      case CONTEST_STATUS.CANCELED:
        return "bg-red-50 hover:bg-red-100 border-l-4 border-red-500";
      default:
        return "hover:bg-zinc-100 dark:hover:bg-zinc-800";
    }
  };

  const handleInvestigationRequest = (contest: TableDataProps) => {
    setInvestigationModal({
      isOpen: true,
      contestData: contest,
    });
  };

  const handleConfirmInvestigation = async () => {
    if (!investigationModal.contestData) return;
    
    const contestId = investigationModal.contestData.id;
    setInvestigationLoading((prev) => ({ ...prev, [contestId]: true }));

    try {
      const { ok } = await generateInvestigationContest(contestId);
      if (!ok) return;
      toast.success("Apuração iniciada com sucesso!");

      updateFront((prev) => ({
        ...prev,
        data: prev?.data?.map((contest) =>
          contest.id === contestId
            ? { ...contest, has_investigation: 1 }
            : contest
        ),
      }));

      setInvestigationModal({
        isOpen: false,
        contestData: null,
      });
    } finally {
      setInvestigationLoading((prev) => ({ ...prev, [contestId]: false }));
    }
  };

  const handleStatusChangeRequest = (
    contest: TableDataProps,
    newStatus: number,
    newStatusName: string
  ) => {
    setConfirmModal({
      isOpen: true,
      contestData: contest,
      newStatus,
      newStatusName,
    });
  };

  const handleConfirmStatusChange = async () => {
    if (!confirmModal.contestData) return;

    const contestUuid = confirmModal.contestData.uuid;
    setStatusUpdateLoading((prev) => ({ ...prev, [contestUuid]: true }));

    try {
      const { ok } = await updateContestStatus(contestUuid, {
        contest_status_id: confirmModal.newStatus,
      });
      if (!ok) return;
      toast.success(
        `Concurso ${confirmModal.newStatusName.toLowerCase()} com sucesso!`
      );

      updateFront((prev) => ({
        ...prev,
        data: prev?.data?.map((contest) =>
          contest.uuid === contestUuid
            ? {
                ...contest,
                contest_status_id: confirmModal.newStatus,
                contest_status: {
                  id: confirmModal.newStatus,
                  name: confirmModal.newStatusName,
                },
              }
            : contest
        ),
      }));

      setConfirmModal({
        isOpen: false,
        contestData: null,
        newStatus: 0,
        newStatusName: "",
      });
    } finally {
      setStatusUpdateLoading((prev) => ({ ...prev, [contestUuid]: false }));
    }
  };

  useEffect(() => {
    if (contestId) {
      const decodedId = atob(contestId);
      const contest = dataApi?.data.find(
        (item) => item.id === Number(decodedId)
      );
      if (contest) {
        setOpenDialog({ type: "show", uuid: contest.uuid });
      }
    }
  }, [contestId, dataApi?.data]);

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Loteria</TableHead>
            <TableHead>Concurso</TableHead>
            <TableHead>Data de validade</TableHead>
            <TableHead className="text-center">Status</TableHead>
            <TableHead className="text-center">Ativo</TableHead>
            <TableHead>Criado por</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data.map((item, index) => {
            return (
              <TableRow 
                key={item?.id}
                className={cn("cursor-pointer transition-colors", getContestRowColor(item?.contest_status_id))}
              >
                <TableCell>
                  <span
                    className="px-2 py-1 rounded text-white text-sm font-medium"
                    style={{ backgroundColor: item?.lottery?.color }}
                  >
                    {item?.lottery?.name}
                  </span>
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.name}
                </TableCell>
                <TableCell>
                  {item?.validation_date
                    ? Mask.transformMaskBirthdayInBr(item?.validation_date)
                    : "Não informado"}
                </TableCell>
                <TableCell className="text-center">
                  <Badge
                    variant="outline"
                    className={getStatusColor(item?.contest_status_id)}
                  >
                    {item?.contest_status?.name}
                  </Badge>
                </TableCell>
                <TableCell>
                  <ComponentSwitchToggleStatus
                    defaultChecked={item?.is_active}
                    params={{
                      func: setStatusContests,
                      funcParams: item?.uuid,
                    }}
                    updateFront={() =>
                      updateFront((prev) => {
                        return {
                          ...prev,
                          data: prev?.data?.map((values) => {
                            if (values?.uuid === item?.uuid)
                              return {
                                ...values,
                                is_active: !values?.is_active,
                              };
                            return values;
                          }),
                        };
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  <div className="flex flex-col text-sm leading-tight">
                    <span className="font-medium">
                      {item?.creator?.name ?? "-"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(item?.created_at)}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="flex justify-center">
                  <div className="flex gap-2 w-full justify-center flex-wrap">
                    <If condition={item?.has_investigation === 0}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleInvestigationRequest(item)}
                        disabled={investigationLoading[item.id]}
                        className="h-8 w-auto px-2 text-xs bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100"
                      >
                        {investigationLoading[item.id] ? (
                          <>
                            <Loader className="h-3 w-3 animate-spin mr-1" />
                            Criando...
                          </>
                        ) : (
                          <>
                            <FileSearch className="h-3 w-3 mr-1" />
                            Criar Apuração
                          </>
                        )}
                      </Button>
                    </If>
                    <If
                      condition={
                        item?.contest_status_id !== CONTEST_STATUS.FINISHED &&
                        item?.contest_status_id !== CONTEST_STATUS.CANCELED
                      }
                    >
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          handleStatusChangeRequest(item, CONTEST_STATUS.FINISHED, "Finalizado")
                        }
                        disabled={statusUpdateLoading[item.uuid]}
                        className="h-8 w-auto px-2 text-xs bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Finalizar
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          handleStatusChangeRequest(item, CONTEST_STATUS.CANCELED, "Cancelado")
                        }
                        disabled={statusUpdateLoading[item.uuid]}
                        className="h-8 w-auto px-2 text-xs bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
                      >
                        <XCircle className="h-3 w-3 mr-1" />
                        Cancelar
                      </Button>
                    </If>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setSalesTotalizerModal({
                          isOpen: true,
                          contestData: item,
                        })
                      }
                      className="h-8 w-auto px-2 text-xs bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100"
                    >
                      <BarChart3 className="h-3 w-3 mr-1" />
                      Vendas
                    </Button>

                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                        >
                          <MoreHorizontal className="size-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <If condition={hasPermission("show-contest-details")}>
                          <DropdownMenuItem
                            onSelect={() =>
                              setOpenDialog({ type: "show", uuid: item?.uuid })
                            }
                          >
                            <Eye className="size-4 mr-2" />
                            Visualizar
                          </DropdownMenuItem>
                        </If>
                        <If condition={hasPermission("edit-contest")}>
                          <DropdownMenuItem
                            onSelect={() =>
                              setOpenDialog({ type: "edit", uuid: item?.uuid })
                            }
                          >
                            <Pencil className="size-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                        </If>
                        <If condition={hasPermission("delete-contest")}>
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-600"
                            onSelect={() =>
                              setOpenDialog({ type: "delete", uuid: item?.uuid })
                            }
                          >
                            <Trash className="size-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </If>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <If condition={hasPermission("edit-contest")}>
                      <UpdateBtn
                        hideTrigger
                        open={isDialogOpen("edit", item?.uuid)}
                        onOpenChange={handleDialogChange("edit", item?.uuid)}
                        uuid={item?.uuid}
                        updateFront={(response) => {
                          updateFront((prev) => {
                            prev?.data?.splice(index, 1, response);
                            return {
                              ...prev,
                              data: [...prev.data],
                            };
                          });
                        }}
                      />
                    </If>
                    <If condition={hasPermission("delete-contest")}>
                      <DeleteBtn
                        hideTrigger
                        open={isDialogOpen("delete", item?.uuid)}
                        onOpenChange={handleDialogChange("delete", item?.uuid)}
                        params={{
                          func: deleteContestsData,
                          funcParams: item?.uuid,
                        }}
                        updateFront={() =>
                          updateFront((prev) => ({
                            ...prev,
                            data: [
                              ...prev?.data?.filter(
                                (data) => data?.uuid !== item?.uuid
                              ),
                            ],
                          }))
                        }
                        referenceLabel={item?.name}
                      />
                    </If>

                    <div>
                      <If condition={hasPermission("show-contest-details")}>
                        <ShowBtn
                          hideTrigger
                          open={isDialogOpen("show", item?.uuid)}
                          onOpenChange={handleDialogChange("show", item?.uuid)}
                          labels={{ title: "Visualizar Concurso" }}
                          params={{
                            func: getShowContests,
                            funcParams: item?.uuid,
                          }}
                          className="sm:max-w-[700px]"
                          render={(responseApi: TableDataProps) => (
                            <ScrollArea className="h-[70vh]">
                              <div className="w-full h-auto">
                                <div className="grid grid-cols-3 gap-4 mb-6">
                                  <Grid.Item name="Loteria" flex={0}>
                                    {responseApi?.lottery?.name}
                                  </Grid.Item>
                                  <Grid.Item name="Concurso" flex={0}>
                                    {responseApi?.name}
                                  </Grid.Item>
                                  <Grid.Item name="Data de validade" flex={0}>
                                    {responseApi?.validation_date
                                      ? Mask.transformMaskBirthdayInBr(
                                          responseApi?.validation_date
                                        )
                                      : "Não informado"}
                                  </Grid.Item>
                                </div>
                                <div className="flex flex-col gap-4">
                                  {responseApi?.product_classifications && responseApi.product_classifications.length > 0 && (
                                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                      <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3">
                                        Resumo dos Bolões
                                      </h4>
                                      <div className="grid grid-cols-3 gap-3 mb-3">
                                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
                                          <span className="text-2xl font-bold text-blue-600">
                                            {responseApi.product_classifications.length}
                                          </span>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">Total de Bolões</p>
                                        </div>
                                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
                                          <span className="text-2xl font-bold text-green-600">
                                            {responseApi.product_classifications.reduce(
                                              (acc, p) => acc + (p?.product_classification?.quota_quantity || 0),
                                              0
                                            )}
                                          </span>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">Total de Cotas</p>
                                        </div>
                                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
                                          <span className="text-2xl font-bold text-purple-600">
                                            {responseApi.product_classifications.reduce(
                                              (acc, p) => acc + (p?.product_classification?.game_quantity || 0),
                                              0
                                            )}
                                          </span>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">Total de Jogos</p>
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-2 gap-3 mb-4">
                                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
                                          <span className="text-xl font-bold text-orange-600">
                                            {Mask.formatCurrency(
                                              responseApi.product_classifications.reduce(
                                                (acc, p) => acc + (p?.product_classification?.sale_price || 0),
                                                0
                                              )
                                            )}
                                          </span>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">Valor Kit</p>
                                        </div>
                                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
                                          <span className="text-xl font-bold text-emerald-600">
                                            {Mask.formatCurrency(responseApi?.prize || 0)}
                                          </span>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">Valor Total</p>
                                        </div>
                                      </div>
                                      <div className="space-y-2">
                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Detalhamento por Produto:</p>
                                        <div className="grid grid-cols-1 gap-2">
                                          {responseApi.product_classifications.map((product, idx) => (
                                            <div
                                              key={idx}
                                              className="flex items-center justify-between bg-white dark:bg-gray-800 rounded px-3 py-2 text-sm"
                                            >
                                              <span className="font-medium text-gray-700 dark:text-gray-300">
                                                {product?.product_name} - {product?.product_classification?.name}
                                              </span>
                                              <div className="flex items-center gap-3">
                                                <Badge variant="outline" className="text-xs">
                                                  {product?.product_classification?.quota_quantity} cotas
                                                </Badge>
                                                <span className="text-green-600 font-semibold">
                                                  {Mask.formatCurrency(product?.product_classification?.sale_price || 0)}
                                                </span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  <Grid.Item name="Bolões - Detalhes Completos" flex={0}>
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Produto</TableHead>
                                          <TableHead>Qtd. Jogos</TableHead>
                                          <TableHead>Qtd. Dezenas</TableHead>
                                          <TableHead>Qtd. Cotas</TableHead>
                                          <TableHead>Preço de venda</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {responseApi?.product_classifications?.map(
                                          (product, index) => (
                                            <TableRow key={index}>
                                              <TableCell>
                                                {product?.product_name} -{" "}
                                                {
                                                  product
                                                    ?.product_classification
                                                    ?.name
                                                }
                                              </TableCell>
                                              <TableCell className="text-center">
                                                {
                                                  product
                                                    ?.product_classification
                                                    ?.game_quantity
                                                }
                                              </TableCell>
                                              <TableCell className="text-center">
                                                {
                                                  product
                                                    ?.product_classification
                                                    ?.dez_quantity
                                                }
                                              </TableCell>
                                              <TableCell className="text-center">
                                                {
                                                  product
                                                    ?.product_classification
                                                    ?.quota_quantity
                                                }
                                              </TableCell>
                                              <TableCell className="text-center">
                                                {Mask.formatCurrency(
                                                  product
                                                    ?.product_classification
                                                    ?.sale_price
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          )
                                        )}
                                      </TableBody>
                                    </Table>
                                  </Grid.Item>
                                </div>
                              </div>
                            </ScrollArea>
                          )}
                          loader={<CreateLoaderInput lines={4} />}
                        />
                      </If>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {dataApi?.data.length < 1 && (
            <TableRow>
              <TableCell colSpan={6}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      <Dialog
        open={confirmModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmModal({
              isOpen: false,
              contestData: null,
              newStatus: 0,
              newStatusName: "",
            });
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Confirmar Mudança de Status</DialogTitle>
            <DialogDescription>
              Você tem certeza que deseja alterar o status deste concurso?
            </DialogDescription>
          </DialogHeader>

          {confirmModal.contestData && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">
                  Resumo do Concurso
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="font-medium">Concurso:</span>
                    <p>{confirmModal.contestData.name}</p>
                  </div>
                  <div>
                    <span className="font-medium">Loteria:</span>
                    <p>{confirmModal.contestData.lottery?.name}</p>
                  </div>
                  <div>
                    <span className="font-medium">Data de Validade:</span>
                    <p>
                      {confirmModal.contestData.validation_date
                        ? Mask.transformMaskBirthdayInBr(
                            confirmModal.contestData.validation_date
                          )
                        : "Não informado"}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium">Status Atual:</span>
                    <Badge
                      variant="outline"
                      className={getStatusColor(
                        confirmModal.contestData.contest_status_id
                      )}
                    >
                      {confirmModal.contestData.contest_status?.name}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center p-4 bg-blue-50 rounded-lg">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">
                    Status será alterado para:
                  </p>
                  <Badge
                    variant="outline"
                    className={getStatusColor(confirmModal.newStatus)}
                  >
                    {confirmModal.newStatusName}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setConfirmModal({
                  isOpen: false,
                  contestData: null,
                  newStatus: 0,
                  newStatusName: "",
                })
              }
              disabled={
                statusUpdateLoading[confirmModal.contestData?.uuid || ""]
              }
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmStatusChange}
              disabled={
                statusUpdateLoading[confirmModal.contestData?.uuid || ""]
              }
              className={cn(
                "flex gap-2",
                confirmModal.newStatus === CONTEST_STATUS.FINISHED
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              )}
            >
              {statusUpdateLoading[confirmModal.contestData?.uuid || ""] ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Alterando...
                </>
              ) : (
                <>
                  {confirmModal.newStatus === CONTEST_STATUS.FINISHED ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  Confirmar {confirmModal.newStatusName}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={investigationModal.isOpen} onOpenChange={(open) => {
        if (!open) {
          setInvestigationModal({
            isOpen: false,
            contestData: null,
          });
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Confirmar Criação de Apuração</DialogTitle>
            <DialogDescription>
              Você tem certeza que deseja iniciar a apuração deste concurso?
            </DialogDescription>
          </DialogHeader>
          
          {investigationModal.contestData && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Resumo do Concurso</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="font-medium">Concurso:</span>
                    <p>{investigationModal.contestData.name}</p>
                  </div>
                  <div>
                    <span className="font-medium">Loteria:</span>
                    <p>{investigationModal.contestData.lottery?.name}</p>
                  </div>
                  <div>
                    <span className="font-medium">Data de Validade:</span>
                    <p>{investigationModal.contestData.validation_date 
                      ? Mask.transformMaskBirthdayInBr(investigationModal.contestData.validation_date)
                      : "Não informado"}</p>
                  </div>
                  <div>
                    <span className="font-medium">Status:</span>
                    <Badge 
                      variant="outline" 
                      className={getStatusColor(investigationModal.contestData.contest_status_id)}
                    >
                      {investigationModal.contestData.contest_status?.name}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-center p-4 bg-blue-50 rounded-lg">
                <div className="text-center">
                  <FileSearch className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Ação:</strong> Criar Apuração
                  </p>
                  <p className="text-xs text-gray-500">
                    Esta ação irá processar os resultados e gerar a apuração do concurso.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInvestigationModal({
                isOpen: false,
                contestData: null,
              })}
              disabled={investigationLoading[investigationModal.contestData?.id || 0]}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmInvestigation}
              disabled={investigationLoading[investigationModal.contestData?.id || 0]}
              className="flex gap-2 bg-blue-600 hover:bg-blue-700"
            >
              {investigationLoading[investigationModal.contestData?.id || 0] ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Criando Apuração...
                </>
              ) : (
                <>
                  <FileSearch className="h-4 w-4" />
                  Confirmar e Criar Apuração
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {salesTotalizerModal.contestData && (
        <SalesTotalizerModal
          isOpen={salesTotalizerModal.isOpen}
          onClose={() =>
            setSalesTotalizerModal({
              isOpen: false,
              contestData: null,
            })
          }
          contestId={salesTotalizerModal.contestData.id}
          contestName={salesTotalizerModal.contestData.name}
          lotteryName={salesTotalizerModal.contestData.lottery?.name || ""}
          lotteryColor={salesTotalizerModal.contestData.lottery?.color || "#6B7280"}
        />
      )}
    </>
  );
};
